import { useEffect, useState } from 'react';
import Layout from '../components/Layout.jsx';
import { api, getUser, FRANJAS } from '../api.js';

const fmt = (n) => '$ ' + new Intl.NumberFormat('es-CO').format(n ?? 0);
const hoy = () => new Date().toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long' });

export default function Dashboard() {
  const user = getUser();
  const [turno, setTurno] = useState(null);
  const [reporte, setReporte] = useState(null);
  const [recientes, setRecientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [horarioAhora, setHorarioAhora] = useState(null);

  const cargar = async () => {
    setLoading(true);
    try {
      const r = await api.turnoAbierto();
      setTurno(r.turno);
      const dia = await api.reporteDia();
      setReporte(dia);
      if (r.turno) {
        const m = await api.movimientos(r.turno.id);
        setRecientes(m.movimientos.slice(0, 5));
      }
      try {
        const h = await api.horarioActual();
        setHorarioAhora(h);
      } catch {}
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const abrir = async () => {
    setLoading(true);
    try { await api.abrirTurno(); await cargar(); } finally { setLoading(false); }
  };

  const t = (tipo) => reporte?.totales?.find((x) => x.tipo === tipo) ?? { n: 0, monto: 0, cantidad: 0 };
  const franjaInfo = horarioAhora ? FRANJAS.find((f) => f.codigo === horarioAhora.franja) : null;
  const asignada = horarioAhora?.asignacion?.usuario_nombre ?? null;
  const fueraDeTurno = asignada && user?.nombre && asignada.trim().toLowerCase() !== user.nombre.trim().toLowerCase();

  return (
    <Layout>
      {/* Hero */}
      <div className="card-ink mb-8 grain relative overflow-hidden">
        <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full opacity-25 blur-3xl pointer-events-none" style={{ background: 'radial-gradient(closest-side, #C8E847, transparent)' }} />
        <div className="relative flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-lime mb-3">{hoy()}</div>
            {franjaInfo && (
              <div className="inline-flex items-center gap-2 mb-3">
                <span className="pill-lime">Turno {franjaInfo.short} · {franjaInfo.label}</span>
                {asignada
                  ? <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-paper/70">Asignada: {asignada}</span>
                  : <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-paper/60">Sin asignación</span>}
              </div>
            )}
            <h1 className="text-3xl md:text-5xl font-bold tracking-tight leading-tight">
              {turno ? '¡Buen turno!' : '¿Listo para abrir?'}
            </h1>
            <p className="text-paper/80 mt-3 max-w-lg">
              {turno
                ? <>Turno abierto por <span className="text-lime font-medium">{turno.usuario_nombre}</span>{turno.franja ? ` · franja ${turno.franja}` : ''} · {new Date(turno.fecha_apertura).toLocaleString('es-CO', { hour: 'numeric', minute: '2-digit', hour12: true })}</>
                : 'Hola ' + (user?.nombre ?? '') + '. Abre el turno para empezar a registrar consumos del día.'}
            </p>
            {!turno && fueraDeTurno && (
              <div className="mt-3 inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-[12px]" style={{ background: 'rgba(232,181,71,0.18)', border: '1px solid rgba(232,181,71,0.35)' }}>
                <span>⚠</span>
                <span>Según el horario, este turno le toca a <b>{asignada}</b>, no a {user?.nombre}.</span>
              </div>
            )}
          </div>
          <div className="flex gap-3">
            {!turno && (
              <button onClick={abrir} className="btn-accent" disabled={loading}>
                {loading ? 'Abriendo…' : 'Abrir turno'}
              </button>
            )}
            {turno && (
              <a href="/cuadre" className="btn-accent">Cuadre y cierre →</a>
            )}
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Stat
          label="Ventas hoy"
          valor={fmt(t('venta').monto)}
          sub={`${t('venta').n ?? 0} movimientos · ${t('venta').cantidad ?? 0} unidades`}
          icon="💰"
          glow="rgba(27, 42, 78, 0.12)"
        />
        <Stat
          label="Cortesías hoy"
          valor={`${t('cortesia').cantidad ?? 0} ud`}
          sub={`${t('cortesia').n ?? 0} entregas internas`}
          icon="🍵"
          glow="rgba(200, 232, 71, 0.4)"
          accent
        />
        <Stat
          label="Mermas hoy"
          valor={`${t('merma').cantidad ?? 0} ud`}
          sub={`${t('merma').n ?? 0} reportes`}
          icon="⚠️"
          glow="rgba(232, 181, 71, 0.25)"
          muted
        />
      </div>

      {/* Acciones rápidas + recientes */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card lg:col-span-2">
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="eyebrow mb-1">Acciones rápidas</p>
              <h2 className="text-xl font-semibold tracking-tight">¿Qué necesitas registrar?</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <QuickAction href="/servicios" icon="🛏️" label="Vender servicio" sub="Sencilla / Junior / Pres." />
            <QuickAction href="/vender" icon="🛒" label="Vender productos" sub="POS visual con fotos" />
            <QuickAction href="/cuadre" icon="📒" label="Cuadre del turno" sub="Egresos + cierre" />
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <p className="eyebrow">Últimos movimientos</p>
            {turno && <a href="/movimientos" className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted hover:text-ink transition">Ver todos →</a>}
          </div>
          {!turno && <p className="text-muted text-sm">Sin turno abierto.</p>}
          {turno && recientes.length === 0 && <p className="text-muted text-sm">Aún no hay movimientos en este turno.</p>}
          <div className="space-y-3">
            {recientes.map((m) => (
              <div key={m.id} className="flex items-start justify-between gap-3 pb-3 border-b border-line/70 last:border-0 last:pb-0">
                <div className="min-w-0">
                  <div className="font-medium tracking-tight truncate">{m.producto}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted mt-0.5">
                    {m.tipo} · {m.cantidad}ud {m.unidad ? `· ${m.unidad}` : ''}
                  </div>
                </div>
                <div className={`font-mono text-xs whitespace-nowrap ${m.tipo === 'venta' ? 'text-ink' : 'text-muted'}`}>
                  {m.monto > 0 ? fmt(m.monto) : '—'}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}

function Stat({ label, valor, sub, icon, accent, muted, glow }) {
  const bg = accent ? 'card-lime' : muted ? 'card-flat bg-bone/60' : 'stat-card';
  return (
    <div className={`${bg} relative overflow-hidden`}>
      {glow && <div className="absolute -right-12 -top-12 w-40 h-40 rounded-full blur-3xl pointer-events-none" style={{ background: glow }} />}
      <div className="relative flex items-start justify-between mb-4">
        <span className="eyebrow">{label}</span>
        <span className="text-2xl">{icon}</span>
      </div>
      <div className="relative text-3xl md:text-4xl font-semibold tracking-tight leading-none">{valor}</div>
      <div className="relative font-mono text-[11px] text-muted mt-2">{sub}</div>
    </div>
  );
}

function QuickAction({ href, icon, label, sub }) {
  return (
    <a href={href} className="rounded-2xl bg-bone hover:bg-line transition-all duration-200 p-4 flex items-center gap-3 group">
      <span className="text-2xl">{icon}</span>
      <div className="min-w-0">
        <div className="font-semibold tracking-tight text-sm group-hover:translate-x-0.5 transition">{label}</div>
        <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted mt-0.5">{sub}</div>
      </div>
    </a>
  );
}
